import type { RegionComponent, RegionDetail, MapVenue, RegionNode } from '../../../../api/regions';

import { RegionDetailSidebar } from './RegionDetailSidebar';
import { buildColorMap } from './RegionHierarchyTree';
import { flattenById, regionChainNames } from './regionRoot';
import { RegionSubregionList } from './RegionSubregionList';
import { RegionTerritoryCard } from './RegionTerritoryCard';
import { RegionVenueList } from './RegionVenueList';

interface Props {
  detail: RegionDetail;
  regions: RegionNode[];
  boundary?: GeoJSON.Geometry | null;
  components?: RegionComponent[];
  venues?: MapVenue[];
  onGone: () => void;
}

const columnStyle = { display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 } as const;

/** The two-column body of the region detail page (territory + subregions +
 *  member venues on the left; profile / activity / danger zone on the right). */
export function RegionDetailGrid({
  detail,
  regions,
  boundary = null,
  components = [],
  venues = [],
  onGone,
}: Props) {
  const byId = flattenById(regions);
  const color = buildColorMap(regions).get(detail.id) ?? 'var(--nb-primary)';
  const subs = byId.get(detail.id)?.children ?? [];

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
        gap: 14,
      }}
    >
      <div style={columnStyle}>
        <RegionTerritoryCard boundary={boundary} color={color} venueCount={detail.venueCount} />
        <RegionSubregionList subs={subs} color={color} />
        <RegionVenueList venues={venues} chainOf={(rid) => regionChainNames(byId, rid)} />
      </div>

      <RegionDetailSidebar
        detail={detail}
        components={components}
        regions={regions}
        color={color}
        onGone={onGone}
      />
    </div>
  );
}
